import { useResource } from '@landsbankinn/l16n';
import {
  Box,
  Flex,
  InputContainer,
  InputValidationMessage,
  Label,
  SSNNumberInput,
} from '@landsbankinn/ui-kit';
import { useRef, useState } from 'react';
import { useScreenSize } from 'src/hooks/useScreenSize';
import {
  AllowedLoginType,
  useApplicationClientContext,
  useApplicationServerContext,
  type LoginServerState,
} from '../../../../state';
import { DebugUserSelector } from '../DebugUserSelector';
import { ExternalAuthTypesLink } from '../ExternalAuthTypesLink';
import { LoginButtons } from '../LoginButtons';
import { LoginForm } from '../LoginForm';
import { res } from './AudkenniApp.res';
import { AudkenniAppLoadingWithCode } from './AudkenniAppCode';
import { useAudkenniAppSubmit } from './useAudkenniAppSubmit';
import { useAudkenniAppValidation } from './useAudkenniAppValidation';

export function AudkenniApp({
  loginButtonHidden,
}: {
  loginButtonHidden?: boolean;
}) {
  const { r } = useResource();
  const { isMobile } = useScreenSize();
  const [submitWithPostCode, setSubmitWithPostCode] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState<boolean>(false);
  const kennitalaRef = useRef<HTMLInputElement>(null);

  const [serverState] = useApplicationServerContext() as [LoginServerState];
  const [, clientDispatch] = useApplicationClientContext();
  const { onKennitalaChanged, audkenniAppState, invalidResource, onXsrf } =
    useAudkenniAppValidation();
  const submit = useAudkenniAppSubmit({
    onCode: (code: string) => {
      // set code to show to the user
      setSubmitWithPostCode(code);
    },
    onSubmitting: (isSubmitting: boolean) => {
      // some submit running
      setIsSubmitting(isSubmitting);
      clientDispatch({ type: 'setIsSubmitting', payload: { isSubmitting } });
    },
    onError: (error: string) => {
      // error
      clientDispatch({
        type: 'setLoginErrorState',
        payload: { errorMessage: error },
      });
    },
    onXsrf: (xsrfToken: string) => {
      // got a new xsrf token
      onXsrf(xsrfToken);
    },
  });

  return (
    <LoginForm
      form={serverState.forms.audkenniApp}
      fieldValueProvider={(name) => {
        if (name === 'xsrf' && audkenniAppState.xsrf) {
          return audkenniAppState.xsrf;
        }
        return serverState.forms.audkenniApp.fields[name].value;
      }}
      onSubmit={(e) => {
        if (audkenniAppState.kennitala.validationState !== 'valid') {
          setShowValidation(true);
          return;
        }
        e.preventDefault();
        if (!isSubmitting) {
          submit(
            audkenniAppState.kennitala.value,
            serverState.forms.audkenniApp.method,
            audkenniAppState.xsrf,
          );
        }
      }}
    >
      {isSubmitting && (
        <AudkenniAppLoadingWithCode code={submitWithPostCode || ''} />
      )}
      {!isSubmitting && (
        <>
          <Box marginBottom={isMobile ? '0' : '36'}>
            <Flex
              direction="col"
              mainAxisAlignment="space between"
              grow="1"
              paddingTop="36"
            >
              <Box marginBottom="20">
                <InputContainer
                  id="kennitala"
                  variant="text input col with validation"
                  validationState={audkenniAppState.kennitala.validationState}
                >
                  <Label>{r(res.kennitala)}</Label>
                  <SSNNumberInput
                    name={serverState.forms.audkenniApp.fields.kennitala.name}
                    autoFocus={false}
                    value={audkenniAppState.kennitala.value}
                    variant="default"
                    placeholder=""
                    showMask={false}
                    align="left"
                    ref={kennitalaRef}
                    onValueChange={(v) => {
                      onKennitalaChanged(v.value);
                    }}
                  />
                  {showValidation && (
                    <InputValidationMessage
                      invalidMessage={r(invalidResource)}
                      unsetMessage={r(res.validationKennitalaRequired)}
                    />
                  )}
                </InputContainer>
              </Box>
              <DebugUserSelector
                inputref={kennitalaRef}
                debugValues={
                  serverState.forms.audkenniApp.fields.kennitala.debugValues
                }
                onUserSelect={({ value }) => {
                  onKennitalaChanged(value);
                }}
              />
              <ExternalAuthTypesLink
                label={r(res.externalAuthTypesLinkLabel)}
              />
            </Flex>
          </Box>
          <LoginButtons
            showRejectButton={serverState.isConsentFlow}
            showLoginButton={!loginButtonHidden}
            isValid={audkenniAppState.kennitala.validationState === 'valid'}
            loginType={AllowedLoginType.AudkenniApp}
          />
        </>
      )}
    </LoginForm>
  );
}
